import React, { useState, useEffect } from 'react';
import { encodeBase64, decodeBase64 } from '../base64';

const owner = process.env.REACT_APP_GITHUB_OWNER;
const repo = process.env.REACT_APP_GITHUB_REPO;
const branch = process.env.REACT_APP_GITHUB_BRANCH || 'main';
const token = process.env.REACT_APP_GITHUB_TOKEN;

async function getFile(path) {
  const res = await fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}?ref=${branch}`, {
    headers: token ? { Authorization: `token ${token}` } : {}
  });
  if (!res.ok) return null;
  return res.json();
}

async function updateFile(path, content, message, sha) {
  return fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}`, {
    method: 'PUT',
    headers: {
      Authorization: `token ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ message, content, branch, sha })
  });
}

function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');
}

function safeFilename(name) {
  const parts = name.split('.');
  const ext = parts.pop();
  return `${slugify(parts.join('.'))}.${ext}`;
}

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

function DocumentationForm({ event, onClose, onSaved }) {
  const [docs, setDocs] = useState([]);
  const [deletedUrls, setDeletedUrls] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const list = [];
    for (let i = 1; i <= 10; i++) {
      const num = String(i).padStart(2, '0');
      const url = event[`imgdoc${num}`];
      const credit = event[`imgdoc${num}Credit`];
      if (url || credit) {
        list.push({ url: url || '', credit: credit || '', file: null, preview: '' });
      } else if (i === 1) {
        list.push({ url: '', credit: '', file: null, preview: '' });
      } else {
        break;
      }
    }
    if (list.length === 0) {
      list.push({ url: '', credit: '', file: null, preview: '' });
    }
    setDocs(list);
    setDeletedUrls([]);
  }, [event]);

  const handleFileChange = (idx, file) => {
    setDocs((prev) => {
      const next = [...prev];
      const doc = { ...next[idx] };
      if (doc.url) {
        setDeletedUrls((d) => [...d, doc.url]);
        doc.url = '';
      }
      if (doc.preview) {
        URL.revokeObjectURL(doc.preview);
      }
      if (file) {
        doc.preview = URL.createObjectURL(file);
      } else {
        doc.preview = '';
      }
      doc.file = file;
      next[idx] = doc;
      return next;
    });
  };

  const handleCreditChange = (idx, credit) => {
    setDocs((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], credit };
      return next;
    });
  };

  const handleRemove = (idx) => {
    setDocs((prev) => {
      const doc = prev[idx];
      if (doc.url) {
        setDeletedUrls((d) => [...d, doc.url]);
      }
      if (doc.preview) {
        URL.revokeObjectURL(doc.preview);
      }
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [{ url: '', credit: '', file: null, preview: '' }];
    });
  };

  const handleAdd = () => {
    setDocs((prev) => [...prev, { url: '', credit: '', file: null, preview: '' }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const updated = [...docs];
    for (let i = 0; i < updated.length; i++) {
      const doc = updated[i];
      if (doc.file) {
        if (doc.file.size > MAX_FILE_SIZE) {
          setError('Images must be less than 1MB');
          setSaving(false);
          return;
        }
        const safeName = safeFilename(doc.file.name);
        const path = `public/events/${event.slug}/${safeName}`;
        const reader = new FileReader();
        const data = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(doc.file);
        });
        await updateFile(path, data, `Add documentation ${event.id} [skip netlify]`);
        updated[i].url = `/${path.replace('public/', '')}`;
      }
    }
    // update events.json
    const file = await getFile('events.json');
    if (!file) {
      setSaving(false);
      onClose();
      return;
    }
    const events = JSON.parse(decodeBase64(file.content));
    const idx = events.findIndex((e) => e.id === event.id);
    if (idx < 0) {
      setSaving(false);
      onClose();
      return;
    }
    const evt = events[idx];
    const active = updated.filter((d) => d.url);
    for (let i = 0; i < active.length; i++) {
      const num = String(i + 1).padStart(2, '0');
      const doc = active[i];
      evt[`imgdoc${num}`] = doc.url;
      evt[`imgdoc${num}Credit`] = doc.credit || '';
    }
    for (let i = active.length + 1; i <= 10; i++) {
      const num = String(i).padStart(2, '0');
      delete evt[`imgdoc${num}`];
      delete evt[`imgdoc${num}Credit`];
    }
    events[idx] = evt;
    await updateFile('events.json', encodeBase64(JSON.stringify(events, null, 2)), `Update docs ${event.id}`, file.sha);
    for (const url of deletedUrls) {
      const imgPath = `public${url}`;
      const imgFile = await getFile(imgPath);
      if (imgFile) {
        await deleteFile(imgPath, `Delete documentation ${event.id} [skip netlify]`, imgFile.sha);
      }
    }
    setSaving(false);
    onSaved(evt);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-20">
      <form className="bg-white p-4 flex flex-col gap-2 w-96 overflow-y-auto max-h-full" onSubmit={handleSubmit}>
        <h2 className="text-xl mb-2">Documentation</h2>
        {docs.map((doc, idx) => (
          <div key={idx} className="border p-2 flex flex-col gap-1 mb-2">
            {(doc.preview || doc.url) && (
              <img
                src={doc.preview || doc.url}
                alt="doc"
                className="w-20 h-20 object-cover"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(idx, e.target.files[0])}
            />
            <input
              className="border p-1"
              value={doc.credit}
              onChange={(e) => handleCreditChange(idx, e.target.value)}
              placeholder="Credit"
            />
            <button type="button" className="text-sm underline" onClick={() => handleRemove(idx)}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" className="underline text-left" onClick={handleAdd}>
          Add another
        </button>
        {error && <div className="text-red-500 text-sm">{error}</div>}
        <div className="flex gap-2 mt-2">
          <button type="submit" className="bg-black text-white p-2" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          <button type="button" className="p-2" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default DocumentationForm;
